import React from 'react'

import { BaseView } from '@/components/base'
import { useTheme } from '@/hooks/useTheme'

interface TransactionSkeletonProps {
  count?: number
  containerStyles?: React.CSSProperties
}

export const TransactionSkeleton: React.FC<TransactionSkeletonProps> = ({
  count = 3,
  containerStyles,
}) => {
  const theme = useTheme()

  const SkeletonBox: React.FC<{
    width: string | number
    height: string | number
    borderRadius?: string | number
  }> = ({ width, height, borderRadius = '4px' }) => (
    <BaseView
      style={{
        width,
        height,
        backgroundColor:
          theme.colors.skeleton?.backgroundColor ||
          theme.colors.background.secondaryGray + '40',
        borderRadius,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <BaseView
        style={{
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '100%',
          height: '100%',
          background: `linear-gradient(90deg, transparent, ${theme.colors.skeleton?.foregroundColor || 'rgba(255,255,255,0.1)'}, transparent)`,
          animation: 'skeleton-shimmer 1.5s infinite',
        }}
      />
    </BaseView>
  )

  return (
    <BaseView gap={12} style={containerStyles}>
      <style>
        {`
          @keyframes skeleton-shimmer {
            0% { left: -100%; }
            100% { left: 100%; }
          }
        `}
      </style>

      {Array.from({ length: count }).map((_, index) => (
        <BaseView
          key={index}
          px={12}
          py={16}
          style={{
            backgroundColor: 'transparent',
            borderRadius: '24px',
            minHeight: '76px',
          }}
        >
          <BaseView
            row
            align="center"
            justify="space-between"
            style={{ height: '100%' }}
          >
            {/* Avatar skeleton */}
            <BaseView row align="center" gap={12} flex={1}>
              <SkeletonBox width={48} height={48} borderRadius="50%" />

              {/* Text content skeleton */}
              <BaseView flex={1} gap={8} justify="center">
                <SkeletonBox width="65%" height={16} borderRadius="8px" />
                <SkeletonBox width="45%" height={14} borderRadius="6px" />
              </BaseView>
            </BaseView>

            {/* Amount and status skeleton */}
            <BaseView align="flex-end" gap={6} justify="center">
              <SkeletonBox width={90} height={16} borderRadius="8px" />
              <SkeletonBox width={70} height={14} borderRadius="6px" />
            </BaseView>
          </BaseView>
        </BaseView>
      ))}
    </BaseView>
  )
}
